// Per-row state machine for the Live In Club toggle interaction. One
// actor per visible row. Drives the row's loading / success / undo /
// error UI states; the page coordinates the cross-row "single toast"
// invariant via the NotificationProvider, so this machine only knows
// its own row's transitions.
//
// State lifecycle:
//   idle ──TAP──► submitting
//                  │
//                  ├─SUBMIT_SUCCESS─► undo_window
//                  │                   │
//                  │                   ├─UNDO_CLICK──► reverting
//                  │                   │                │
//                  │                   │                ├─REVERT_SUCCESS─► idle
//                  │                   │                └─REVERT_FAILURE─► idle (with error)
//                  │                   ├─WINDOW_ELAPSED ► idle (committed)
//                  │                   └─SUPPLANT ─────► idle (committed)
//                  └─SUBMIT_FAILURE─► error
//                                     │
//                                     ├─RETRY_CLICK ──► submitting
//                                     ├─ERROR_TIMEOUT ► idle
//                                     └─SUPPLANT ────► idle
//
// `target_status` carries the status the row is transitioning toward so
// the undo path can synthesize the inverse without consulting the page.

import { assign, type StateValueFrom, setup } from 'xstate';

import type { StatusValue } from '../../domain/status';

export interface TalentRowContext {
	talent_id: string;
	target_status: StatusValue | null;
	/**
	 * The row's `lastInClubAt` value at the moment of the TAP, captured so
	 * the undo path can restore it on the server (item 3, option G1). Lives
	 * in the actor's context so it survives the row component remounting
	 * when the section swap happens.
	 */
	prior_last_in_club_at: string | null;
	error: Error | null;
}

export type TalentRowEvent =
	| {
			type: 'TAP';
			target_status: StatusValue;
			prior_last_in_club_at: string | null;
	  }
	| { type: 'SUBMIT_SUCCESS' }
	| { type: 'SUBMIT_FAILURE'; error: Error }
	| { type: 'UNDO_CLICK' }
	| { type: 'WINDOW_ELAPSED' }
	| { type: 'REVERT_SUCCESS' }
	| { type: 'REVERT_FAILURE'; error: Error }
	| { type: 'RETRY_CLICK' }
	| { type: 'ERROR_TIMEOUT' }
	| { type: 'SUPPLANT' };

export const talentRowMachine = setup({
	types: {
		context: {} as TalentRowContext,
		events: {} as TalentRowEvent,
		input: {} as { talent_id: string },
	},
}).createMachine({
	id: 'talentRow',
	initial: 'idle',
	context: ({ input }) => ({
		talent_id: input.talent_id,
		target_status: null,
		prior_last_in_club_at: null,
		error: null,
	}),
	states: {
		idle: {
			on: {
				TAP: {
					target: 'submitting',
					actions: assign({
						target_status: ({ event }) => event.target_status,
						prior_last_in_club_at: ({ event }) => event.prior_last_in_club_at,
						error: () => null,
					}),
				},
			},
		},
		submitting: {
			on: {
				SUBMIT_SUCCESS: 'undo_window',
				SUBMIT_FAILURE: {
					target: 'error',
					actions: assign({ error: ({ event }) => event.error }),
				},
			},
		},
		undo_window: {
			on: {
				UNDO_CLICK: 'reverting',
				WINDOW_ELAPSED: 'idle',
				SUPPLANT: 'idle',
			},
		},
		reverting: {
			on: {
				REVERT_SUCCESS: 'idle',
				REVERT_FAILURE: {
					target: 'idle',
					actions: assign({ error: ({ event }) => event.error }),
				},
			},
		},
		error: {
			on: {
				RETRY_CLICK: 'submitting',
				ERROR_TIMEOUT: 'idle',
				SUPPLANT: 'idle',
			},
		},
	},
});

export type TalentRowState = StateValueFrom<typeof talentRowMachine>;
